import {
  BaseQueryFn,
  FetchBaseQueryError,
  QueryDefinition
} from '@reduxjs/toolkit/dist/query';
import { SubscriptionOptions } from '@reduxjs/toolkit/dist/query/core/apiState';
import { UseLazyQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { kc as keycloak, updateToken } from 'src/keycloak';
import { setLicenseExpired } from 'src/redux/slices/auth';
import { setAlert } from 'src/redux/slices/snackbar';
import { useSelector } from 'src/redux/store';
import { APITags } from 'src/services/utils';
import { ALERT } from 'src/types/enum';
import {
  ErrorCodes,
  ErrorContext,
  ErrorMessages
} from 'src/utils/errorMappings';
import logger from 'src/utils/logger';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import {
  INVALID_TOKEN_STATUS,
  INVALID_USER_STATUS,
  MAX_REQUEST_RETRIES,
  RETRYABLE_HTTP_STATUS_CODES,
  SESSION_EXPIRED_STATUS
} from 'src/utils/utils';
import { getRetryDelayMs } from './getRetryDelayMs';

const useLazyQuery = <T, R>({
  api,
  errorContext = ErrorContext.GENERAL,
  options = {},
  maxRetries
}: {
  api: UseLazyQuery<QueryDefinition<T, BaseQueryFn, APITags, R>>;
  errorContext?: ErrorContext;
  options?: SubscriptionOptions;
  maxRetries?: number;
}) => {
  const { t } = useTranslation();
  const { isLicenseExpired } = useSelector((state) => state.auth);
  const [_fetchData, result, lastPromiseInfo] = api({ ...options });
  const dispatch = useDispatch();
  const { isError, data, error } = result;
  const isInternetConnected = useMemo(
    () => !!window?.navigator?.onLine,
    [window?.navigator?.onLine]
  );
  const effectiveMaxRetries = maxRetries ?? (api as any).maxRetries ?? MAX_REQUEST_RETRIES;

  let _errorCallback: MutableRefObject<
    ((error: FetchBaseQueryError) => void) | undefined
  > = useRef<((error: FetchBaseQueryError) => void) | undefined>();

  let _successCallback: MutableRefObject<((data: R) => void) | undefined> =
    useRef<((data: R) => void) | undefined>();

  let _fallbackMsg: MutableRefObject<string | undefined> = useRef<
    string | undefined
  >();
  const retryFailedCountRef = useRef(0);

  useEffect(() => {
    if (isError) {
      const _error = error as FetchBaseQueryError | undefined;
      const err_status = error as
        | FetchBaseQueryError
        | { status: 'INVALID_USER' }
        | { status: 'INVALID_TOKEN' }
        | { status: 'SESSION_EXPIRED' };
      const status = (_error as any)?.originalStatus || (_error as any)?.data?.status;
      // Retry, in case for INVALID USER or  error_status
      if (
        (err_status?.status === INVALID_USER_STATUS ||
          err_status?.status === INVALID_TOKEN_STATUS ||
          err_status?.status === SESSION_EXPIRED_STATUS
        ) &&
        retryFailedCountRef.current < effectiveMaxRetries
      ) {
        const delay = getRetryDelayMs(err_status?.status as any, retryFailedCountRef.current, err_status);
        retryFailedCountRef.current += 1;
        void updateToken(keycloak).then(() => {
          setTimeout(() => {
            logger.log(`Retrying... Attempt ${retryFailedCountRef.current}`);
            _fetchData(lastPromiseInfo?.lastArg); // Retry with the same arguments
          }, delay);
        });
      } else if (
        (RETRYABLE_HTTP_STATUS_CODES.includes(status) ||
          (_error as any)?.status === 'FETCH_ERROR') &&
        retryFailedCountRef.current < effectiveMaxRetries
      ) {
        const delay = getRetryDelayMs(status, retryFailedCountRef.current, _error);
        retryFailedCountRef.current += 1;
        setTimeout(() => {
          logger.log(`Retrying... Attempt ${retryFailedCountRef.current}`);
          _fetchData(lastPromiseInfo?.lastArg); // Retry with the same arguments
        }, delay);
      } else {
        retryFailedCountRef.current = 0; // Reset for future requests
        dispatch(
          setAlert({
            type: ALERT.ERROR,
            msg: t(
              isInternetConnected
                ? ErrorMessages[errorContext][_error?.status] ||
                    ErrorMessages[ErrorContext.GENERAL][_error?.status] ||
                    _fallbackMsg?.current ||
                    T.internalServerError
                : T.noInternet
            ),
            isConnectedToInternet: isInternetConnected
          })
        );
        if (_errorCallback.current) _errorCallback.current(_error);
      }
    }
  }, [
    isError,
    errorContext,
    dispatch,
    error,
    _errorCallback,
    isInternetConnected
  ]);

  useEffect(() => {
    if (_successCallback.current && data) {
      _successCallback.current(data);
    }
  }, [_successCallback, data]);

  const fetchData = useCallback(
    ({
      params,
      fallbackMsg,
      errorCallback,
      successCallback
    }: {
      params: T;
      fallbackMsg?: string;
      errorCallback?: (error: any) => void;
      successCallback?: (data: R) => void;
    }) => {
      _errorCallback.current = errorCallback;
      _successCallback.current = successCallback;
      _fallbackMsg.current = fallbackMsg;
      return _fetchData({ ...params });
    },
    [_fetchData]
  );

  return [fetchData, result, lastPromiseInfo] as const;
};

export default useLazyQuery;
